import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, HelpCircle, Search, X } from "lucide-react";
import { useState, useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_CATEGORIES, FEATURED_FAQ } from "@/data/faqs";
import { SERVICE_FAQS } from "@/data/serviceFaqs";
import { getService } from "@/lib/content";

// Convert a rich answer (string or array of blocks) into a flat searchable string
const flattenAnswer = (a) => {
  if (typeof a === "string") return a;
  if (!Array.isArray(a)) return "";
  return a
    .map((b) =>
      typeof b === "string" ? b : b && b.list ? b.list.join(" ") : ""
    )
    .join(" ");
};

// Build a flat, deduplicated search index across all FAQs on the site
const buildIndex = () => {
  const entries = [];
  // Featured
  entries.push({
    q: FEATURED_FAQ.question,
    a: FEATURED_FAQ.answer,
    sourceLabel: "Featured",
    sourceHref: "/faq",
    accent: "featured",
  });
  // Main FAQ hub categories
  FAQ_CATEGORIES.forEach((cat) => {
    cat.items.forEach((item, idx) => {
      entries.push({
        q: item.q,
        a: flattenAnswer(item.a),
        sourceLabel: cat.label,
        sourceHref: `/faq#${cat.slug}`,
        anchor: `${cat.slug}-${idx}`,
        accent: "hub",
      });
    });
  });
  // Per-service FAQs
  Object.entries(SERVICE_FAQS).forEach(([slug, pack]) => {
    const svc = getService(slug);
    const label = svc ? svc.title : slug;
    pack.items.forEach((item, idx) => {
      entries.push({
        q: item.q,
        a: flattenAnswer(item.a),
        sourceLabel: label,
        sourceHref: `/services/${slug}#faq-${idx}`,
        accent: "service",
      });
    });
  });
  return entries;
};

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [query, setQuery] = useState("");

  const index = useMemo(() => buildIndex(), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return index
      .filter(
        (e) =>
          e.q.toLowerCase().includes(q) || e.a.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [query, index]);

  // Extract a short snippet from the answer around the first match
  const snippet = (text, q) => {
    if (!text) return "";
    const lower = text.toLowerCase();
    const idx = lower.indexOf(q.toLowerCase());
    if (idx < 0) return text.slice(0, 160) + (text.length > 160 ? "..." : "");
    const start = Math.max(0, idx - 60);
    const end = Math.min(text.length, idx + q.length + 100);
    return (
      (start > 0 ? "..." : "") +
      text.slice(start, end) +
      (end < text.length ? "..." : "")
    );
  };

  return (
    <main data-testid="faq-page" className="bg-white">
      <Seo
        title="Knowledge hub & FAQs"
        description="Straight answers about content, video, branding and marketing for the UK highways and infrastructure sector. HighwaysIndustry.com's editorial knowledge base, searchable across every service."
        path="/faq"
      />
      {/* ─────────── Hero ─────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">FAQs</span>
          </div>
          <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              data-testid="faq-headline"
              className="lg:col-span-8 font-heading font-black text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter text-slate-900"
            >
              Straight
              <br />
              <span className="text-accent-blue italic">answers.</span>
            </motion.h1>
            <p className="lg:col-span-4 text-lg text-slate-600 leading-relaxed">
              The questions we get asked most. If you can&apos;t find yours,
              the shortest route to an answer is a{" "}
              <Link
                to="/positioning-review"
                className="text-accent-blue underline underline-offset-4 hover:text-cyan-500"
              >
                20-minute discovery call
              </Link>
              .
            </p>
          </div>

          {/* Global FAQ search */}
          <div className="mt-10 max-w-2xl relative">
            <label htmlFor="faq-search" className="sr-only">
              Search all FAQs
            </label>
            <Search
              className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              size={18}
            />
            <input
              id="faq-search"
              type="text"
              data-testid="faq-search"
              placeholder="Search every FAQ across the site..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-14 pr-12 py-4 rounded-full border border-slate-200 bg-white text-slate-900 text-base focus:outline-none focus:ring-2 focus:ring-accent-blue placeholder:text-slate-400 shadow-sm"
            />
            {query && (
              <button
                type="button"
                data-testid="faq-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 transition-colors"
              >
                <X size={18} />
              </button>
            )}
            {query && (
              <div className="mt-2 text-xs text-slate-500">
                {results.length} result{results.length === 1 ? "" : "s"} across
                the whole site
              </div>
            )}
          </div>

          {/* Category jump nav */}
          <nav
            data-testid="faq-jump-nav"
            className={`mt-8 flex flex-wrap gap-2 ${query ? "hidden" : ""}`}
          >
            {FAQ_CATEGORIES.map((cat) => (
              <a
                key={cat.slug}
                href={`#${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-heading font-semibold uppercase tracking-widest transition-all ${
                  activeCategory === cat.slug
                    ? "bg-slate-900 border-slate-900 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-accent-blue hover:text-accent-blue"
                }`}
              >
                {cat.label}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* ─────────── Search results (only shown when searching) ─────────── */}
      {query && (
        <section
          data-testid="faq-results"
          className="py-16 md:py-20 border-b border-slate-200"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Search results
              </span>
            </div>
            {results.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-slate-600 text-lg">
                  No FAQs match &ldquo;
                  <span className="font-semibold text-slate-900">{query}</span>
                  &rdquo;.
                </p>
                <p className="mt-3 text-slate-500 text-sm">
                  Try a shorter or different phrase, or{" "}
                  <Link
                    to="/positioning-review"
                    className="text-accent-blue underline underline-offset-4"
                  >
                    book a 20-minute call
                  </Link>{" "}
                  and we&apos;ll answer it directly.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                {results.map((r, idx) => (
                  <Link
                    key={idx}
                    to={r.sourceHref}
                    data-testid={`faq-result-${idx}`}
                    className="group block p-5 md:p-6 rounded-xl border border-slate-200 bg-white hover:border-accent-blue hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue">
                            {r.sourceLabel}
                          </span>
                          {r.accent === "service" && (
                            <span className="text-[10px] uppercase tracking-widest text-slate-400">
                              Service FAQ
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-base md:text-lg text-slate-900 group-hover:text-accent-blue transition-colors">
                          {r.q}
                        </h3>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">
                          {snippet(r.a, query)}
                        </p>
                      </div>
                      <ArrowUpRight
                        className="text-slate-300 group-hover:text-accent-blue transition-colors flex-shrink-0 mt-1"
                        size={18}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─────────── Featured "Top FAQ" (dark) ─────────── */}
      {!query && (
        <section
        data-testid="featured-faq"
        className="dark-section relative bg-slate-950 py-16 md:py-24 overflow-hidden"
      >
        {/* Subtle grid backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3">
                <Sparkles size={16} className="text-cyan-400" />
                <span className="label-eyebrow text-cyan-300">
                  The one everyone asks
                </span>
              </div>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-white">
                Read this
                <br />
                <span className="text-accent-blue italic">first.</span>
              </h2>
              <p className="mt-6 text-white/60 text-sm leading-relaxed">
                Nine out of ten discovery calls start here. If you only take
                one thing from the FAQ, take this.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-8 rounded-3xl border border-cyan-400/30 bg-white/[0.03] backdrop-blur p-6 md:p-10"
            >
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-white leading-tight">
                {FEATURED_FAQ.question}
              </h3>
              <p className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed">
                {FEATURED_FAQ.answer}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/positioning-review"
                  data-testid="featured-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-blue text-white font-heading font-semibold hover:bg-cyan-500 transition-colors text-sm"
                >
                  Book a discovery call
                  <ArrowUpRight size={14} />
                </Link>
                <Link
                  to="/industry-reach"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white font-heading font-semibold hover:border-accent-blue hover:text-accent-blue transition-colors text-sm"
                >
                  See the reach numbers
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      )}

      {/* ─────────── Category sections ─────────── */}
      {!query && FAQ_CATEGORIES.map((cat, catIdx) => (
        <section
          key={cat.slug}
          id={cat.slug}
          data-testid={`faq-category-${cat.slug}`}
          className={`py-16 md:py-24 border-b border-slate-200 scroll-mt-24 ${
            catIdx % 2 === 1 ? "bg-slate-50" : "bg-white"
          }`}
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-16">
            {/* Left: sticky category header */}
            <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  {cat.eyebrow}
                </span>
              </div>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-slate-900">
                {cat.label}.
              </h2>
              <p className="mt-4 text-sm text-slate-500">
                {cat.items.length}{" "}
                {cat.items.length === 1 ? "question" : "questions"} in this
                section
              </p>
            </div>

            {/* Right: accordion */}
            <div className="lg:col-span-8">
              <Accordion
                type="single"
                collapsible
                className="w-full"
                data-testid={`accordion-${cat.slug}`}
              >
                {cat.items.map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`${cat.slug}-${idx}`}
                    className="border-b border-slate-200 last:border-b-0"
                  >
                    <AccordionTrigger
                      data-testid={`faq-q-${cat.slug}-${idx}`}
                      className="text-left py-6 hover:no-underline group"
                    >
                      <span className="flex items-start gap-3 pr-4">
                        <HelpCircle
                          size={18}
                          className="text-accent-blue mt-0.5 flex-shrink-0"
                          strokeWidth={2}
                        />
                        <span className="font-heading font-bold text-base md:text-lg text-slate-900 group-hover:text-accent-blue transition-colors">
                          {item.q}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <div className="pl-8">
                        <p className="text-slate-700 text-base md:text-lg leading-relaxed">
                          {item.a}
                        </p>
                        {item.cta && (
                          <Link
                            to={item.cta.href}
                            data-testid={`faq-cta-${cat.slug}-${idx}`}
                            className="mt-4 inline-flex items-center gap-2 text-sm font-heading font-semibold text-accent-blue hover:text-cyan-500 transition-colors group/cta"
                          >
                            {item.cta.label}
                            <ArrowUpRight
                              size={14}
                              className="group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 transition-transform"
                            />
                          </Link>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      ))}

      {/* ─────────── Bottom CTA ─────────── */}
      <section
        data-testid="faq-bottom-cta"
        className="dark-section relative bg-slate-950 py-20 md:py-28 overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-cyan-300">
              Still have questions?
            </span>
            <span className="h-px w-12 bg-accent-blue" />
          </div>
          <h2 className="mt-6 font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter text-white">
            Better than an FAQ:
            <br />
            <span className="text-accent-blue italic">
              a 20-minute call.
            </span>
          </h2>
          <p className="mt-6 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Every business is different. Book a discovery call and we&apos;ll
            answer your specific questions about content, positioning and
            what a campaign would look like for your team.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/positioning-review"
              data-testid="faq-book-call"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-accent-blue text-white font-heading font-semibold hover:bg-cyan-500 transition-colors text-base"
            >
              Book a discovery call
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white font-heading font-semibold hover:border-accent-blue hover:text-accent-blue transition-colors text-base"
            >
              Send us a message
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
